import { Module } from 'vuex';
import { LinkManagementState } from '@/modules/tickets/incidents/link-management/store/link-management.state';
import { CategoryTyping } from '@/modules/tickets/shared/domain/category-typing';
import { Contact } from '@/modules/contacts/shared/domain/contact';
import { Typing, TypingLevels } from '@/modules/tickets/shared/domain/typing';
import { User } from '@/modules/users/domain/user';
import { UserGetters } from '@/modules/users/store/user.getters';
import ticketsRepository from '@/modules/tickets/shared/repositories/tickets.repository';
import { Tickets } from '@/modules/shared/shared/domain/constants/constants';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { EventBus } from '@/main';
import { EventBusMessages } from '@/modules/shared/shared/domain/constants/event-bus-messages';
import { LinkManagementGetters } from '@/modules/tickets/incidents/link-management/store/link-management.getters';
import { LinkManagementMutations } from '@/modules/tickets/incidents/link-management/store/link-management.mutations';
import { LinkManagementData } from '@/modules/tickets/incidents/link-management/domain/link-management-data';
import { LinkManagementActions } from '@/modules/tickets/incidents/link-management/store/link-management.actions';
import { LinkManagementTicket } from '@/modules/tickets/incidents/link-management/domain/link-management-ticket';
import incidentsRepository from '@/modules/tickets/shared/repositories/incidents.repository';
import { Incident } from '@/modules/tickets/incidents/shared/domain/incident';
import { AttachedFile } from '@/modules/tickets/incidents/shared/domain/attached-file';

const linkManagement: Module<LinkManagementState, any> = {
    namespaced: true,
    state: {
        formValues: {
            generalInformation: {
                subCategory: null,
                subject: null,
            },
            categoriesTypings: [],
        },
        incidents: [],
    },
    getters: {
        [LinkManagementGetters.GET_FORM_VALUES](
            state,
        ): {
            generalInformation: LinkManagementData;
            categoriesTypings: CategoryTyping;
        } {
            return state.formValues;
        },
        [LinkManagementGetters.GET_OPEN_INCIDENTS](state) {
            return state.incidents;
        },
    },
    mutations: {
        [LinkManagementMutations.SET_FORM_VALUES](state, formValues) {
            state.formValues = formValues;
        },
        [LinkManagementMutations.SET_INCIDENTS](state, incidents: Incident[]) {
            state.incidents = incidents;
        },
        [LinkManagementMutations.SET_CONTACT_INFORMATION](state, contact: Contact) {
            state.formValues.generalInformation.firstName = contact.name;
            state.formValues.generalInformation.lastName = contact.lastName;
            state.formValues.generalInformation.contactId = contact.id;
            state.formValues.generalInformation.idNumber = contact.ci;
            state.formValues.generalInformation.telephoneNumber = contact.phone;
        },
        [LinkManagementMutations.SET_TYPINGS](state, typings: Typing[]) {
            state.formValues.categoriesTypings = typings.filter(
                typing => typing.level === TypingLevels.CATEGORY_LEVEL,
            );

            const subjects = typings.filter(typing => typing.level === TypingLevels.SUBJECT_LEVEL);
            const subCategories = typings.filter(
                typing => typing.level === TypingLevels.SUBCATEGORY_LEVEL,
            );

            state.formValues.categoriesTypings = state.formValues.categoriesTypings.map(
                category => {
                    return {
                        ...category,
                        subCategoriesTypings: subCategories
                            .filter(subCategory => subCategory.typing === category.id)
                            .map(subCategory => {
                                return {
                                    ...subCategory,
                                    subjectTypings: subjects.filter(
                                        subject => subject.typing === subCategory.id,
                                    ),
                                };
                            }),
                    };
                },
            );
        },
    },
    actions: {
        async [LinkManagementActions.FETCH_VIEW]({
            commit,
            rootGetters,
        }): Promise<RequestResponse> {
            commit(LinkManagementMutations.SET_FORM_VALUES, {
                generalInformation: {
                    subCategory: null,
                    subject: null,
                },
                categoriesTypings: [],
            });
            commit(LinkManagementMutations.SET_INCIDENTS, []);
            const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
            commit(LinkManagementMutations.SET_CONTACT_INFORMATION, currentUser.contact);
            try {
                const typings = await ticketsRepository.getTypings(
                    currentUser.contact.customer.id,
                    Tickets.LinkManagement.name,
                );
                commit(LinkManagementMutations.SET_TYPINGS, typings);
                const incidents = await incidentsRepository.getOpenIncidents(
                    currentUser.contact.customer.id,
                    Tickets.LinkManagement.name,
                );
                commit(LinkManagementMutations.SET_INCIDENTS, incidents);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [LinkManagementActions.CREATE_TICKET](
            { rootGetters },
            information: LinkManagementData,
        ): Promise<RequestResponse> {
            try {
                const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
                const contact = currentUser.contact;
                const customer = contact.customer;
                const ticket: LinkManagementTicket = {
                    description: information.description || '',
                    typing: information.subject!,
                    ticketType: Tickets.LinkManagement,
                    affectedLocation: information.affectedLocation!,
                    affectedService: information.affectedService!,
                    affectedSupplier: information.affectedSupplier!,
                    attachments: [{ fileName: information.attachment!.name }],
                    customer: {
                        id: customer.id,
                        name: customer.name,
                        category: customer.category!,
                        contact: {
                            id: contact.id,
                            name: contact.name,
                            lastName: contact.lastName,
                            email: contact.email,
                            ci: contact.ci!,
                        },
                    },
                };

                const createdTicket: { id: number; attachments: AttachedFile[] } = (
                    await ticketsRepository.createTicket(ticket)
                ).data;

                const attachment = createdTicket.attachments[0];
                const formData = new FormData();
                formData.set(
                    'file',
                    information.attachment!,
                    attachment.fileName + attachment.extension,
                );
                formData.set('mailAttachmentId', '' + attachment.mailAttachmentId);
                formData.set('ticketAttachmentId', '' + attachment.ticketAttachmentId);
                await incidentsRepository.uploadIncidentAttachment(createdTicket.id, formData);

                EventBus.$emit(
                    EventBusMessages.SHOW_SUCCESS,
                    `Su ticket fue creado exitosamente (Serial Nº ${createdTicket.id})`,
                );
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
    },
};

export default linkManagement;
